import React, { useCallback, useEffect, useMemo } from 'react';
import ReactFlow, {
    Background,
    BackgroundVariant,
    Controls,
    type Node,
    type NodeChange,
    type NodeDimensionChange,
    useNodesState,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { createApiUrl } from '../../../config/api';
import { usePlotViewStore } from '../../../stores/plotViewStore';
import PlotNode from './PlotNode';

interface PlotItem {
  file_name: string;
  file_type: string;
  plot_json?: string;
}

interface PlotCanvasProps {
  plots: PlotItem[];
  tabId: string;
  sessionId?: string;
  userID?: string;
}

// Register custom node types
const nodeTypes = {
  plotNode: PlotNode,
};

const PlotCanvas: React.FC<PlotCanvasProps> = ({ plots, tabId, sessionId, userID }) => {
  const { getTabLayout, setCanvasPosition, setPlotSize, initializeTabLayout } = usePlotViewStore();
  const layout = getTabLayout(tabId);

  // Initialize layout when plots change
  useEffect(() => {
    if (plots.length > 0) {
      initializeTabLayout(
        tabId,
        plots.map((p) => p.file_name)
      );
    }
  }, [tabId, plots, initializeTabLayout]);

  // Build image URLs for non-Plotly plots
  const plotImageUrls = useMemo(() => {
    const urls: Record<string, string> = {};
    plots.forEach((plot) => {
      if (!plot.plot_json && sessionId && userID) {
        urls[plot.file_name] = createApiUrl(
          `/file_handling/users/${userID}/sessions/${sessionId}/files/processed/file_types/plot/${encodeURIComponent(plot.file_name)}`
        );
      }
    });
    return urls;
  }, [plots, sessionId, userID]);

  // Convert plots to React Flow nodes
  const initialNodes: Node[] = useMemo(() => {
    return plots.map((plot, index) => {
      const position = layout.canvasPositions[plot.file_name] || {
        x: (index % 3) * 480 + 50,
        y: Math.floor(index / 3) * 420 + 50,
      };
      const size = layout.plotSizes[plot.file_name] || { width: 450, height: 350 };

      return {
        id: plot.file_name,
        type: 'plotNode',
        position,
        width: size.width,
        height: size.height,
        data: {
          plot,
          imageUrl: plotImageUrls[plot.file_name],
          size,
          onResize: (newSize: { width: number; height: number }) => {
            setPlotSize(tabId, plot.file_name, newSize);
          },
        },
        draggable: true,
      };
    });
  }, [plots, layout.canvasPositions, layout.plotSizes, tabId, setPlotSize, plotImageUrls]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);

  // Sync nodes when initialNodes change (e.g., when layout or plots update)
  useEffect(() => {
    setNodes(initialNodes);
  }, [initialNodes, setNodes]);

  // Handle node changes and cache positions/dimensions
  const handleNodesChange = useCallback(
    (changes: NodeChange[]) => {
      onNodesChange(changes);

      // Save positions and dimensions when nodes are moved or resized
      changes.forEach((change) => {
        if (change.type === 'position' && change.position && change.id) {
          setCanvasPosition(tabId, change.id, change.position);
        }
        // Handle dimension changes from NodeResizer
        if (change.type === 'dimensions' && change.id) {
          const dimensionChange = change as NodeDimensionChange;
          if (dimensionChange.dimensions) {
            setPlotSize(tabId, change.id, {
              width: dimensionChange.dimensions.width,
              height: dimensionChange.dimensions.height,
            });
          }
        }
      });
    },
    [onNodesChange, tabId, setCanvasPosition, setPlotSize]
  );

  return (
    <div className="w-full h-full">
      <ReactFlow
        nodes={nodes}
        onNodesChange={handleNodesChange}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        nodesDraggable={true}
        nodesConnectable={false}
        elementsSelectable={true}
        minZoom={0.2}
        maxZoom={2}
        attributionPosition="bottom-left"
      >
        <Controls />
        <Background variant={BackgroundVariant.Dots} color="#d1d5db" gap={16} size={1.5} />
      </ReactFlow>
    </div>
  );
};

export default PlotCanvas;
